/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import React, { useState, useMemo, useEffect } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';

import {
  LoadedCompContext,
  SessionUpdateContext,
  usePageStateStore,
  useSessionUpdateZustandStore,
} from '@marriott/mi-chat-components';
import { PageContext } from '@marriott/mi-shop-components';
import { pageInitialData, setSubDirectoryPrefix } from '@marriott/mi-shop-components/utils';
import { defineComponentMapping, getAllowedComponentsForNonAuthorMode } from '../import-components';
import operationSignatures from '../_constants/operationSignatures.json';
//import hotelQuickViewCacheConfig from '../_constants/hotelQuickViewCacheConfig.json';
import 'react-loading-skeleton/dist/skeleton.css';
import { useClientEnvVarsStore, useScrollStore } from '@marriott/mi-store-utils';
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  CustomModelClient,
  respGridUtil,
  canUseDOM,
  populatePIdataLayer,
  UserSessionProvider,
  DynamicDCAPageProvider,
  getEnvProp,
} from '@marriott/mi-headless-utils';
/* eslint-enable @typescript-eslint/no-unused-vars */
import { useDCAPageModelStore } from '@marriott/mi-store-utils';
import globalStylesPkg from '@marriott/global-styles/package.json';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useMarriottAIPreviewStore, extractSignInModelFromPageModel } from '@marriott/mi-chat-components';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { envVarKeys } from '../_constants/envVarKeys';
import '../styles/chat.scss';

const globalStylesVersion = globalStylesPkg.version;
declare global {
  interface Window {
    jQuery: any;
    $: any;
    deployedEnvType: string;
    impressionArr: string[];
    impressionTrack: (value: string) => Record<string, string>;
    dataLayer?: Record<string, unknown>;
  }
}

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

const App = function (props: any) {
  const { Component, pageProps } = props;
  // log.debug('[STEP 1] App component', Component, process.env);
  // log.debug('[STEP 1] App pageProps', pageProps);

  // log.debug('_app.tsx client side: ', props, pageProps);
  const rtlPluginTs = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  const {
    sessionData,
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    resolvedUrl,
    requestId,
    currentLocale,
    getFeatureFlag,
    headersData,
    cookies,
    query,
    model,
    isAuthorMode,
    MI_DATALAYER_APP_URL,
    datalayerParams,
    datalayerProperties,
    NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
    interceptorResponse,
    NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
    sessionFetchedData, // session data fetched from productspecific call please maintain the same name
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    apolloEnvVars,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY,
    DEPLOYED_ENV_CONFIG,
    ENABLE_ADV_SEARCH_DATALAYER_CALL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GROUPS_DEV_URL,
    GROUPS_TEST_URL,
    isDTT,
    disableDistanceFromLocation,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    CACHE_CLEAR_URL,
    ENABLE_TARGET_SEGMENT_CALL,
    cnEnvVars,
    isBulgariDomain,
    NEXT_GLOBAL_ASSET_PREFIX,
    NEXT_PUBLIC_BULGARI_URL,
    featureFlagLogo,
    featureFlagAdfZustand,
    featureFlagSerpZustand,
    findHotelsRemoteSPA,
  } = pageProps;

  const preHeader: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-request-id': headersData ? headersData['x-request-id'] : '',
    Cookie: `sessionID=${cookies?.sessionID};UserIdToken=${cookies?.UserIdToken ? cookies?.UserIdToken : ''}`,
    'X-Host': headersData ? headersData['x-host'] : '',
    'accept-language': headersData?.['accept-language'] || process.env['NEXT_PUBLIC_DEFAULT_LANG'] || '',
  };
  const modelAllowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;
  // Wrap with useMemo to avoid recomputing on every render
  const allowedComponents = useMemo(() => {
    if (!model) return undefined;
    if ((modelAllowedComponents?.length ?? 0) > 0 || isAuthorMode) return modelAllowedComponents;
    return getAllowedComponentsForNonAuthorMode(model);
  }, [model, modelAllowedComponents, isAuthorMode]);
  const setEnvVars = useClientEnvVarsStore(state => state.setEnvVars);
  const envVars = { ...apolloEnvVars, ...cnEnvVars };
  envVars['TIP_SSE_ENDPOINT'] = pageProps?.TIP_SSE_ENDPOINT;
  envVars['TIP_SESSION_ENDPOINT'] = pageProps?.TIP_SESSION_ENDPOINT;
  envVars['NEXT_PUBLIC_ENABLE_WEBSDK'] =
    getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_WEBSDK) === 'true' && model?.enableWebSDK === 'true';
  envVars['NEXT_PUBLIC_ENABLE_DCA_V2'] = getEnvProp(envVarKeys.NEXT_PUBLIC_ENABLE_DCA_V2);
  envVars['ENABLE_ACDL'] = getEnvProp(envVarKeys.ENABLE_ACDL);
  envVars['SESSION_GET_CALL_URL_CLIENT'] = pageProps?.SESSION_GET_CALL_URL_CLIENT;
  envVars['NEXT_PUBLIC_SUBMIT_SEARCH_URL'] = pageProps?.NEXT_PUBLIC_SUBMIT_SEARCH_URL;
  envVars['NEXT_PUBLIC_AVAILABILITY_SEARCH_URL'] = pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL;
  envVars['REQUEST_ID'] = requestId;
  envVars['ACCEPT_LANGUAGE'] = currentLocale?.replace('_', '-');
  envVars['DAC_IMG_DOMAIN'] = pageProps?.DAC_IMG_DOMAIN;
  envVars['GOOGLE_MAP_API_KEY'] = pageProps?.GOOGLE_MAP_API_KEY;
  envVars['NEXT_PUBLIC_PREFIX'] = pageProps?.NEXT_PUBLIC_PREFIX;
  envVars['NEXT_PUBLIC_BULGARI_URL'] = pageProps?.NEXT_PUBLIC_BULGARI_URL;
  envVars['SESSION_APP_POST_CALL_URL'] = pageProps?.SESSION_APP_POST_CALL_URL;
  envVars['SESSION_APP_POST_CALL_URL'] = pageProps?.SESSION_APP_POST_CALL_URL;
  envVars['BAIDU_MAP_API_KEY'] = pageProps?.BAIDU_MAP_API_KEY;
  envVars['TARGET_SEGMENT_PROPERTY_TOKEN'] = pageProps?.TARGET_SEGMENT_PROPERTY_TOKEN;
  envVars['TARGET_SEGMENT_MBOX_NAME'] = pageProps?.TARGET_SEGMENT_MBOX_NAME;
  envVars['IS_LOCAL_DEV'] = pageProps?.IS_LOCAL_DEV;
  envVars['NEXT_PUBLIC_HOME_URL'] = pageProps?.NEXT_PUBLIC_HOME_URL;
  envVars['NEXT_PUBLIC_FIND_HOTELS_URL'] = pageProps?.NEXT_PUBLIC_FIND_HOTELS_URL;
  envVars['NEXT_PUBLIC_AVAILABILITY_SEARCH_URL'] = pageProps?.NEXT_PUBLIC_AVAILABILITY_SEARCH_URL;
  envVars['USE_CHAT_MOCK_MODEL'] = pageProps?.USE_CHAT_MOCK_MODEL;
  envVars['SSE_INACTIVITY_TIMEOUT_MS'] = pageProps?.SSE_INACTIVITY_TIMEOUT_MS;
  envVars['EVENT_STREAM_RESPONSE_TIMEOUT_MS'] = pageProps?.EVENT_STREAM_RESPONSE_TIMEOUT_MS;
  envVars['TIP_API_TIMEOUT_MS'] = pageProps?.TIP_API_TIMEOUT_MS;
  envVars['MAX_CHAT_COUNT'] = pageProps?.MAX_CHAT_COUNT;
  envVars['CHAT_EXPIRATION_DAYS'] = pageProps?.CHAT_EXPIRATION_DAYS;
  envVars['NEXT_PUBLIC_SESSION_TIMEOUT_MS'] = pageProps?.NEXT_PUBLIC_SESSION_TIMEOUT_MS;
  envVars['NEXT_PUBLIC_CHAT_TIMEOUT_REDIRECT_URL'] = pageProps?.NEXT_PUBLIC_CHAT_TIMEOUT_REDIRECT_URL;
  envVars['NEXT_PUBLIC_PRE_PROCESSING_URL'] = pageProps?.NEXT_PUBLIC_PRE_PROCESSING_URL;
  setEnvVars(envVars);

  useEffect(() => {
    if (isAuthorMode) {
      let remoteURL: URL | string;
      try {
        remoteURL = new URL(document.body.dataset.remoteUrl || '');
        remoteURL = remoteURL.origin;
      } catch (e) {
        console.info('Handled remoteUrl for Non remote SPA author mode');
        remoteURL = '';
      }
      const assetUrl = `${
        process.env['NEXT_PUBLIC_ASSET_DOMAIN'] || remoteURL || 'https://www.marriott.com'
      }${NEXT_GLOBAL_ASSET_PREFIX}`;
      const globalStyle = document.createElement('link');

      globalStyle.rel = 'stylesheet';
      globalStyle.href =
        assetUrl +
        (model?.rtlEnable == 'true'
          ? `global-styles/${globalStylesVersion}/marriot.global.rtl.css`
          : `global-styles/${globalStylesVersion}/marriot.global.css`);
      document.head.appendChild(globalStyle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [sessionDataState, setSessionDataState] = useState<any>(sessionData || sessionFetchedData);

  //This code will populate the PI data related datalayer elements on clientside
  if (canUseDOM) populatePIdataLayer();

  let subDirectoryContextPath = '';

  if (model) {
    subDirectoryContextPath = model.subDirectoryContextPath ? model.subDirectoryContextPath : '';
    setSubDirectoryPrefix(subDirectoryContextPath);
  }

  defineComponentMapping(
    allowedComponents,
    resolvedUrl,
    isAuthorMode,
    currentLocale,
    isBulgariDomain,
    NEXT_PUBLIC_BULGARI_URL,
    pageProps
  );
  const [pageState, setPageState] = useState<any>({ ...pageInitialData, authorMode: isAuthorMode });
  const [loadedCompState, setLaodedCompState] = useState<string[]>([]);
  const setSelectedMemberLevel = useMarriottAIPreviewStore(state => state.setSelectedMemberLevel);
  const setIsAuthenticated = useMarriottAIPreviewStore(state => state.setIsAuthenticated);
  const setSignInDrawerModel = useMarriottAIPreviewStore(state => state.setSignInDrawerModel);
  const memoLoadedCompContextValue = useMemo(() => {
    const updateLoadedComponents = (componentName: string) => {
      if (!loadedCompState.includes(componentName)) {
        setLaodedCompState(preState => {
          return [...preState, componentName];
        });
      }
    };
    return { loadedCompState, updateLoadedComponents };
  }, [loadedCompState]);

  const memoPageContexValue = useMemo(() => {
    const host = headersData?.['x-host'];
    const updateComponentState = ({
      componentName,
      newState,
    }: {
      componentName: string;
      newState: Record<string, any>;
    }) => {
      setPageState((preState: any) => {
        return { ...preState, [componentName]: newState };
      });
    };

    const updatePageErrors = ({ componentName, errors }: { componentName: string; errors: any }) => {
      setPageState((preState: any) => {
        return {
          ...preState,
          pageLevelErrors: {
            ...preState.pageLevelErrors,
            [componentName]: errors,
          },
        };
      });
    };

    const sessionDataNode = sessionData?.data || sessionData?.cacheData?.data;

    if (sessionDataNode) {
      setSelectedMemberLevel(sessionDataNode?.memberLevel);
      setIsAuthenticated(sessionData?.authenticated);
    }

    return {
      pageVal: 'chat',
      featureFlagLogo,
      featureFlagAdfZustand,
      featureFlagSerpZustand,
      pageState,
      setPageState,
      updateComponentState,
      updatePageErrors,
      sessionData: sessionDataState,
      setSessionData: setSessionDataState,
      resolvedUrl,
      getFeatureFlag,
      requestId,
      currentLocale: isAuthorMode ? 'en-US' : currentLocale?.replace('_', '-'),
      headersData,
      cookies,
      query,
      operationSignatures,
      //hotelQuickViewCacheConfig,
      MI_DATALAYER_APP_URL,
      datalayerParams,
      datalayerProperties,
      NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
      NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
      interceptorResponse,
      preHeader,
      SESSION_APP_GET_CALL_URL: `https://${host}${SESSION_APP_GET_CALL_URL}`,
      SESSION_APP_POST_CALL_URL: `https://${host}${SESSION_APP_POST_CALL_URL}`,
      NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
      GOOGLE_MAP_API_KEY,
      BAIDU_MAP_API_KEY,
      DEPLOYED_ENV_CONFIG,
      ENABLE_ADV_SEARCH_DATALAYER_CALL,
      NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
      GROUPS_DEV_URL,
      GROUPS_TEST_URL,
      isDTT: true,
      disableDistanceFromLocation,
      NEXT_PUBLIC_PRE_PROCESSING_URL: `https://${host}${NEXT_PUBLIC_PRE_PROCESSING_URL}`,
      CACHE_CLEAR_URL: `https://${host}${CACHE_CLEAR_URL}`,
      ENABLE_TARGET_SEGMENT_CALL,
      isBulgariDomain,
      NEXT_PUBLIC_BULGARI_URL,
      isAuthorMode,
      isOffline: pageProps?.featureFlags?.EXPERIENCE_MCOM_CHAT_IS_CONVO_AI_APP_OFFLINE || false,
      findHotelsRemoteSPA,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    featureFlagLogo,
    featureFlagAdfZustand,
    featureFlagSerpZustand,
    pageState,
    setPageState,
    sessionData,
    sessionDataState,
    setSessionDataState,
    resolvedUrl,
    getFeatureFlag,
    requestId,
    currentLocale,
    headersData,
    cookies,
    query,
    operationSignatures,
    //hotelQuickViewCacheConfig,
    MI_DATALAYER_APP_URL,
    datalayerParams,
    datalayerProperties,
    NEXT_PUBLIC_PRE_PROCESSING_ADF_URL,
    interceptorResponse,
    preHeader,
    sessionFetchedData,
    NEXT_PUBLIC_PRE_PROCESSING_RELATIVE_ADF_URL,
    SESSION_APP_GET_CALL_URL,
    SESSION_APP_POST_CALL_URL,
    NEXT_PUBLIC_DYNAMIC_ROUTING_URL_CLIENT,
    GOOGLE_MAP_API_KEY,
    BAIDU_MAP_API_KEY,
    DEPLOYED_ENV_CONFIG,
    ENABLE_ADV_SEARCH_DATALAYER_CALL,
    NEXT_PUBLIC_AVAILABILITY_SEARCH_URL,
    GROUPS_DEV_URL,
    GROUPS_TEST_URL,
    isDTT,
    disableDistanceFromLocation,
    NEXT_PUBLIC_PRE_PROCESSING_URL,
    CACHE_CLEAR_URL,
    NEXT_PUBLIC_BULGARI_URL,
    ENABLE_TARGET_SEGMENT_CALL,
    isBulgariDomain,
    findHotelsRemoteSPA,
  ]);
  const pageStateStoreInitialization = usePageStateStore((state: any) => state.pageStateStoreInitialization);

  // Get SessionUpdate Zustand store for initialization
  const sessionUpdateStore = useSessionUpdateZustandStore();

  useEffect(() => {
    // if (
    //   featureFlagAdfZustand?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ||
    //   featureFlagSerpZustand?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND]
    // ) {
    pageStateStoreInitialization(memoPageContexValue);
    // }
  }, [memoPageContexValue, pageStateStoreInitialization]);

  const [interactionsSelected, setInteractionsSelected] = useState<any>({
    availability: false,
    price: false,
    points: false,
    amenities: false,
    brands: false,
    distance: false,
    transportation: false,
    hotelType: false,
    events: false,
    activities: false,
    states: false,
    city: false,
    pagination: false,
    taxesAndFees: false,
    currencyConversion: false,
    sortBy: false,
    quickFilters: false,
    savedProperties: false,
    listOrMapButton: false,
  });

  // Initialize SessionUpdate Zustand store with interactionsSelected state
  useEffect(() => {
    const shouldUseZustand = featureFlagSerpZustand?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

    if (shouldUseZustand && interactionsSelected) {
      const storeHasInteractions = Object.values(sessionUpdateStore.interactionsSelected).some(value => value === true);

      if (!storeHasInteractions) {
        sessionUpdateStore.setInteractionsSelected(interactionsSelected);
      }
    }
  }, [interactionsSelected, featureFlagSerpZustand, sessionUpdateStore]);

  const responsiveGrid = model?.cqItems?.root?.[':items']?.responsivegrid;
  // const respGridModel = respGridUtil(responsiveGrid);

  // Store the full model structure including footer for settings menu
  const fullModelClone = model ? JSON.parse(JSON.stringify(model)) : null; //To deep clone the model
  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  setPageModel(fullModelClone);

  // Extract and store sign-in drawer model for OChatSignedOut component
  const signInModel = extractSignInModelFromPageModel(model);
  if (signInModel) {
    setSignInDrawerModel(signInModel);
  }

  const initialize = useScrollStore(state => state.initialize);
  if (!!responsiveGrid && !isAuthorMode) {
    initialize(responsiveGrid);
  }

  Object.defineProperty(rtlPluginTs, 'name', { value: 'rtlPluginMonorepo' });

  return (
    <UserSessionProvider pageProps={pageProps} includePiData={true}>
      <LoadedCompContext.Provider value={memoLoadedCompContextValue}>
        <PageContext.Provider value={memoPageContexValue}>
          <SessionUpdateContext.Provider value={{ interactionsSelected, setInteractionsSelected }}>
            {/* Add loading Aria Label from AEM gloabl label */}
            {/* <ProgressLoaderProvider coverPage={true} loadingAriaLabel="Please wait, loading content."> */}
            <StyleSheetManager stylisPlugins={pageProps?.model?.rtlEnable === 'true' ? [rtlPluginTs] : []}>
              <Component {...pageProps} />
            </StyleSheetManager>
            {/* </ProgressLoaderProvider> */}
          </SessionUpdateContext.Provider>
        </PageContext.Provider>
      </LoadedCompContext.Provider>
    </UserSessionProvider>
  );
};

export default App;
